import { useState } from 'react';
import { Button, Modal, Typography, message, Tabs, Card, Descriptions, Table } from 'antd';

import http from '../../http';
import MyUploadInput from '../../components/Fields/UploadInput';
import { getReadableDateFromTimeStamp } from '../../utils/common';
import { useTranslation } from 'react-i18next';

export const CalibrationCertificate = ({ record, onChange, sideEffect }) => {
    const { t } = useTranslation();
    const handleDownload = async () => {
        const blob = await http
            .get(
                `/api/v1/machines/machine_registry_files/${record.id}/download_calibration_certificate/`,
                { responseType: 'blob' }
            )
            .then((res) => res.data)
            .catch(() => {
                message.error(t('certificate_failed_to_download'));
            });
        if (!blob) return;
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `calibration-certificate-${record.id}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const hasReport = Object.keys(record?.calibration_report || {}).length > 0;

    return (
        <Modal
            centered
            width={800}
            closable={true}
            open={Boolean(record)}
            okText={t('certificate_download')}
            cancelText='Close'
            cancelButtonProps={{ style: { display: 'none' } }}
            onOk={handleDownload}
            onCancel={() => onChange(null)}
            okButtonProps={{ disabled: !hasReport }}
        >
            <Tabs
                defaultActiveKey='1'
                items={[
                    {
                        key: '1',
                        label: t('certificate_calibration'),
                        children: (
                            <CalibrationReport
                                record={record}
                                onChange={onChange}
                                sideEffect={sideEffect}
                            />
                        ),
                    },
                    {
                        key: '2',
                        label: t('certificate_instrument'),
                        children: (
                            <InstrumentCertificate
                                record={record}
                                onChange={onChange}
                                sideEffect={sideEffect}
                            />
                        ),
                    },
                ]}
            />
        </Modal>
    );
};

const { Title } = Typography;

function CalibrationReport({ record, onChange, sideEffect }) {
    const [file, setFile] = useState(null);
    const { t } = useTranslation();

    const measurementColumns = [
        {
            title: t('certificate_base_measurement_error'),
            children: [
                {
                    title: 'XY [m]',
                    dataIndex: 'base_error_xy',
                    key: 'base_error_xy',
                    render: (text) => (text === null ? '-' : Number(text).toFixed(3)),
                },
                {
                    title: 'Z [m]',
                    dataIndex: 'base_error_z',
                    key: 'base_error_z',
                    render: (text) => (text === null ? '-' : Number(text).toFixed(3)),
                },
            ],
        },
        {
            title: t('certificate_standard_deviation'),
            children: [
                {
                    title: 'XY [m]',
                    dataIndex: 'std_dev_xy',
                    key: 'std_dev_xy',
                    render: (text) => (text === null ? '-' : Number(text).toFixed(3)),
                },
                {
                    title: 'Z [m]',
                    dataIndex: 'std_dev_z',
                    key: 'std_dev_z',
                    render: (text) => (text === null ? '-' : Number(text).toFixed(3)),
                },
            ],
        },
    ];

    const columns = [
        {
            title: t('certificate_point_name'),
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: t('certificate_mgs_measurement_error'),
            children: [
                {
                    title: 'XY [m]',
                    dataIndex: 'xy_error',
                    key: 'xy_error',
                    render: (text) => (text === null ? '-' : Number(text).toFixed(3)),
                },
                {
                    title: 'Z [m]',
                    dataIndex: 'z_error',
                    key: 'z_error',
                    render: (text) => (text === null ? '-' : Number(text).toFixed(3)),
                },
            ],
        },
        {
            title: t('certificate_residual_error_from_base'),
            children: [
                {
                    title: 'XY [m]',
                    dataIndex: 'xy_residual',
                    key: 'xy_residual',
                    render: (text) => (text === null ? '-' : Number(text).toFixed(3)),
                },
                {
                    title: 'Z [m]',
                    dataIndex: 'z_residual',
                    key: 'z_residual',
                    render: (text) => (text === null ? '-' : Number(text).toFixed(3)),
                },
            ],
        },
    ];

    const hasReport = Object.keys(record?.calibration_report || {}).length > 0;

    const updateReport = () => {
        const formData = new FormData();
        formData.append('calibration_report', file.file);
        http.put(
            `/api/v1/machines/machine_registry_files/${record.id}/update_calibration_report/`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        )
            .then((response) => {
                message.success(t('certificate_file_uploaded_successfully'));
                setFile(null);
                onChange({ ...record, ...response.data });
                sideEffect();
            })
            .catch(() => {
                message.error(t('certificate_file_uploaded_failed'));
            });
    };

    const pointData = record?.calibration_report?.check_points ?? [];

    const instrument_info = record?.calibration_report?.metadata?.measurement_instrument_info;
    const operator_info = record?.calibration_report?.metadata?.operator_info;
    const branch = operator_info?.branch_name;
    const pointMeasurementResults = [
        {
            key: '1',
            base_error_xy: record?.calibration_report?.base_error_xy || '-',
            base_error_z: record?.calibration_report?.base_error_z || '-',
            std_dev_xy: record?.calibration_report?.std_dev_xy || '-',
            std_dev_z: record?.calibration_report?.std_dev_z || '-',
        },
    ];

    return (
        <div>
            {hasReport ? (
                <>
                    <Descriptions
                        bordered
                        column={2}
                        size='small'
                        style={{ marginBottom: 24 }}
                        labelStyle={{ width: 200 }}
                    >
                        <Descriptions.Item label={t('certificate_issuer')} span={branch ? 1 : 2}>
                            {operator_info?.company_name}
                        </Descriptions.Item>
                        {branch && (
                            <Descriptions.Item label={t('certificate_branch')} span={1}>
                                {branch}
                            </Descriptions.Item>
                        )}
                        <Descriptions.Item label={t('certificate_operator')} span={2}>
                            {operator_info?.name}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('certificate_date')}>
                            {getReadableDateFromTimeStamp(
                                record?.calibration_report?.metadata?.date
                            )}
                        </Descriptions.Item>
                    </Descriptions>

                    <Descriptions
                        bordered
                        column={1}
                        size='small'
                        style={{ marginBottom: 24 }}
                        labelStyle={{
                            width: 200,
                        }}
                    >
                        <Descriptions.Item label={t('certificate_model')}>
                            {/* {record?.machine_model} */}
                            {/* TODO: Now it's hardcoded later will be dynamic */}
                            MGS V3
                        </Descriptions.Item>
                        <Descriptions.Item label={t('certificate_manufacturer')}>
                            {/* {record?.machine_manufacture} */}
                            {/* TODO: Now it's hardcoded later will be dynamic */}
                            TOTALMASTERS Co. Ltd.
                        </Descriptions.Item>
                        <Descriptions.Item label={t('certificate_machine_id')}>
                            {record?.machine_number}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('certificate_calibration_profile')}>
                            {record?.name?.replace('.json', '')}
                        </Descriptions.Item>
                    </Descriptions>
                    <Descriptions
                        bordered
                        column={2}
                        size='small'
                        style={{ marginBottom: 24 }}
                        labelStyle={{ width: 200 }}
                    >
                        <Descriptions.Item label={t('certificate_measurement_instrument')} span={1}>
                            {instrument_info?.model ?? '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('certificate_manufacturer')} span={1}>
                            {instrument_info?.name ?? '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('certificate_distance_accuracy')} span={2}>
                            {instrument_info?.accuracy}
                        </Descriptions.Item>
                    </Descriptions>

                    <Table
                        dataSource={pointMeasurementResults}
                        columns={measurementColumns}
                        pagination={false}
                        bordered
                        title={() => (
                            <Title level={5} style={{ margin: 0 }}>
                                {t('certificate_point_measurement_result')}
                            </Title>
                        )}
                    />

                    <Table
                        rowKey='name'
                        style={{ marginTop: 24 }}
                        dataSource={pointData}
                        columns={columns}
                        pagination={false}
                        bordered
                        title={() => (
                            <Title level={5} style={{ margin: 0 }}>
                                {t('certificate_point_measurements')}
                            </Title>
                        )}
                    />
                </>
            ) : (
                <Title
                    level={5}
                    style={{
                        textAlign: 'center',
                        border: '1px solid #f0f0f0',
                        borderRadius: 8,
                        padding: '24px 16px',
                        margin: 0,
                    }}
                >
                    {t('certificate_no_calibration_report_found')}
                </Title>
            )}

            <Card
                size='small'
                bordered
                title={t('certificate_update_calibration_report')}
                style={{ marginTop: 24 }}
                bodyStyle={{ display: 'flex', alignItems: 'center', width: '100%' }}
            >
                <div style={{ flex: 1 }}>
                    <MyUploadInput accept='.json' onChange={(file) => setFile(file)} />
                </div>
                <Button
                    disabled={!file}
                    type='primary'
                    style={{ marginLeft: 16 }}
                    onClick={updateReport}
                >
                    {t('calibration_profile_upload_button_text')}
                </Button>
            </Card>
        </div>
    );
}

function InstrumentCertificate({ record, sideEffect, onChange }) {
    const [file, setFile] = useState(null);
    const { t } = useTranslation();

    const onFinish = () => {
        const formData = new FormData();
        formData.append('instrument_certificate', file.file);
        http.put(
            `/api/v1/machines/machine_registry_files/${record.id}/upload_instrument_certificate/`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        )
            .then((response) => {
                message.success(t('certificate_file_uploaded_successfully'));
                setFile(null);
                onChange({ ...record, ...response.data });
                sideEffect();
            })
            .catch(() => {
                message.error(t('certificate_file_upload_failed'));
            });
    };

    return (
        <div>
            <Card size='small' bordered title={t('certificate_instrument_certificate_preview')}>
                {record?.instrument_certificate ? (
                    <iframe
                        src={record?.instrument_certificate}
                        width='100%'
                        height='600px'
                        title='Instrument Certificate'
                        style={{ border: 'none' }}
                    />
                ) : (
                    <Title level={5} style={{ textAlign: 'center' }}>
                        {t('certificate_no_instrument_certificate_found')}
                    </Title>
                )}
            </Card>

            <Card
                size='small'
                bordered
                title={t('certificate_update_instrument_certificate')}
                style={{ marginTop: 24 }}
                bodyStyle={{ display: 'flex', alignItems: 'center', width: '100%' }}
            >
                <div style={{ flex: 1 }}>
                    <MyUploadInput accept='.pdf' onChange={(file) => setFile(file)} />
                </div>
                <Button
                    onClick={onFinish}
                    disabled={!file}
                    type='primary'
                    style={{ marginLeft: 16 }}
                >
                    {t('calibration_profile_upload_button_text')}
                </Button>
            </Card>
        </div>
    );
}
